import { Stack, Typography, useTheme } from "@mui/material";
import { type Control, useFormState } from "react-hook-form";
import {
	ClientPortfolioDataCommodityServiceEnum,
	ClientPortfolioDomainEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type {
	ServicesFormInput,
	ServicesFormOutput,
} from "web_myplann/Portfolio/Forms/ServicesForm/servicesFormSchema";
import { useServicesPriceFields } from "web_myplann/Portfolio/Forms/ServicesForm/useServicesPriceFields";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { DisclosureSection } from "web_myplann/Shared/Components/DisclosureSection";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowHint } from "web_myplann/Shared/Components/FlatRow/FlatRowHint";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

const PARAMETER_FIELDS = [
	"commodityService",
	"tariff",
	"unitOfMeasurement",
	"pricePerUnit",
	"depositAmount",
	"fixedPriceByFrequency",
	"paymentFrequency",
] as const satisfies (keyof ServicesFormInput)[];

type ServicesFormFieldsProps = {
	control: Control<ServicesFormInput, unknown, ServicesFormOutput>;
	disableKeyFields?: boolean;
};

export const ServicesFormFields = ({
	control,
	disableKeyFields = false,
}: ServicesFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	} = useProducerProductOptions(control, ClientPortfolioDomainEnum.Services);

	const commodityServiceOptions = useEnumSelectOptions(
		ClientPortfolioDataCommodityServiceEnum,
		Resources.PortfolioDetail.Services.CommodityService,
		[ClientPortfolioDataCommodityServiceEnum.None],
	);

	const paymentFrequencyOptions = useEnumSelectOptions(
		ClientPortfolioPaymentFrequencyEnum,
		Resources.PortfolioDetail.SharedLabels.Frequency,
	);

	const {
		showMeteredFields,
		showFixedPriceField,
		isReadOnly,
		unitOfMeasurement,
	} = useServicesPriceFields(control);

	const { errors, submitCount } = useFormState({
		control,
		name: PARAMETER_FIELDS,
	});
	const hasParameterError = PARAMETER_FIELDS.some((field) => errors[field]);
	const parametersExpandRequest = hasParameterError ? submitCount : 0;

	const pricePerUnitLabel = unitOfMeasurement
		? t(Resources.Portfolio.AddServices.Fields.pricePerUnitWithUnit, {
				unit: unitOfMeasurement,
			})
		: t(Resources.Portfolio.AddServices.Fields.pricePerUnit);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddServices.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddServices.Fields.name)}
					placeholder={t(
						Resources.Portfolio.AddServices.Placeholders.contractName,
					)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddServices.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					disabled={disableKeyFields}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddServices.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
					isLoading={isLoadingProducers}
					disabled={disableKeyFields}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddServices.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
					disabled={disableKeyFields}
					isLast={!disableKeyFields}
				/>
				{disableKeyFields && (
					<FlatRowHint
						text={t(Resources.Portfolio.Lock.keyFieldsHint)}
						isLast
					/>
				)}
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddServices.Fields,
				)}
			/>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddServices.Sections.values)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<DisclosureSection
					labelCollapsed={t(
						Resources.Portfolio.AddServices.Sections.showParameters,
					)}
					labelExpanded={t(
						Resources.Portfolio.AddServices.Sections.hideParameters,
					)}
					expandRequest={parametersExpandRequest}
				>
					<FlatRowFormSelect
						control={control}
						name="commodityService"
						label={t(Resources.Portfolio.AddServices.Fields.commodityService)}
						options={commodityServiceOptions}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					/>
					<FlatRowFormInput
						control={control}
						name="tariff"
						label={t(Resources.Portfolio.AddServices.Fields.tariff)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					/>
					{showMeteredFields && (
						<>
							<FlatRowFormInput
								control={control}
								name="unitOfMeasurement"
								label={t(
									Resources.Portfolio.AddServices.Fields.unitOfMeasurement,
								)}
								placeholder={t(
									Resources.Portfolio.AddServices.Placeholders
										.unitOfMeasurement,
								)}
								readOnly={isReadOnly}
							/>
							<FlatRowFormInput
								control={control}
								name="pricePerUnit"
								type="float"
								label={pricePerUnitLabel}
								placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
								suffix="Kč"
								readOnly={isReadOnly}
							/>
							<FlatRowFormInput
								control={control}
								name="depositAmount"
								type="float"
								label={t(Resources.Portfolio.AddServices.Fields.depositAmount)}
								placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
								suffix="Kč"
								readOnly={isReadOnly}
							/>
						</>
					)}
					{showFixedPriceField && (
						<FlatRowFormInput
							control={control}
							name="fixedPriceByFrequency"
							type="float"
							label={t(
								Resources.Portfolio.AddServices.Fields.fixedPriceByFrequency,
							)}
							placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
							suffix="Kč"
							readOnly={isReadOnly}
						/>
					)}
					<FlatRowFormSelect
						control={control}
						name="paymentFrequency"
						label={t(Resources.Portfolio.AddServices.Fields.paymentFrequency)}
						options={paymentFrequencyOptions}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
						isLast
					/>
				</DisclosureSection>
			</Stack>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddServices.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddServices.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddServices.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					disabled={disableKeyFields}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddServices.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					disabled={disableKeyFields}
					isLast
				/>
			</Stack>
		</>
	);
};
